import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PhoneOff, Mic, MicOff, Video as VideoIcon, VideoOff, Monitor, MonitorOff, Users, Clipboard, Star, Link as LinkIcon, MessageSquare, ShieldCheck, Send, Flag } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import SafetyReportDialog from '../components/SafetyReportDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const makeWebSocketUrl = (path) => {
  const base = new URL(BACKEND_URL);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = path;
  base.search = `token=${encodeURIComponent(localStorage.getItem('token') || '')}`;
  return base.toString();
};

const scoreItems = ['Communication', 'Role fit', 'Experience', 'Availability'];

export default function VideoRoom({ user }) {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);

  const [roomData, setRoomData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('Preparing room');
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [question, setQuestion] = useState('');
  const [roomMessages, setRoomMessages] = useState([]);
  const [roomMessage, setRoomMessage] = useState('');
  const [showSafetyReport, setShowSafetyReport] = useState(false);
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [scores, setScores] = useState({
    Communication: 0,
    'Role fit': 0,
    Experience: 0,
    Availability: 0,
  });

  const sendSignal = useCallback((message) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  }, []);

  const createPeer = useCallback(() => {
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal({ type: 'ice-candidate', candidate: event.candidate });
      }
    };

    peer.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
      setConnectionStatus('Live interview connected');
    };

    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      if (state === 'connected') setConnectionStatus('Live interview connected');
      if (state === 'connecting') setConnectionStatus('Connecting participants');
      if (state === 'disconnected') setConnectionStatus('Participant disconnected');
      if (state === 'failed') setConnectionStatus('Connection needs retry');
    };

    localStreamRef.current?.getTracks().forEach((track) => {
      peer.addTrack(track, localStreamRef.current);
    });

    peerRef.current = peer;
    return peer;
  }, [sendSignal]);

  const startLocalMedia = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    localStreamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }
    return stream;
  }, []);

  const loadRoom = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.post(`/video/room?booking_id=${bookingId}`);
      setRoomData(res.data);
      if (res.data.workspace) {
        setNotes(res.data.workspace.notes || '');
        setScores((current) => ({ ...current, ...(res.data.workspace.scores || {}) }));
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load video room');
      toast.error('Failed to load video room');
    } finally {
      setIsLoading(false);
    }
  }, [bookingId]);

  const connectSignaling = useCallback(async () => {
    if (!roomData?.signaling_path || socketRef.current) return;

    try {
      await startLocalMedia();
    } catch (err) {
      setError('Camera or microphone permission was blocked. Allow access in the browser, then try again.');
      toast.error('Camera or microphone blocked');
      return;
    }

    const socket = new WebSocket(makeWebSocketUrl(roomData.signaling_path));
    socketRef.current = socket;

    socket.onopen = () => {
      setConnectionStatus('Waiting for the other participant');
      createPeer();
    };

    socket.onmessage = async (event) => {
      const message = JSON.parse(event.data);

      if (message.type === 'room-chat') {
        setRoomMessages((current) => [...current, message]);
        return;
      }
      if (message.type === 'moderation-error') {
        toast.error(message.message || 'That message could not be sent');
        return;
      }

      let peer = peerRef.current || createPeer();

      if (message.type === 'participants') {
        setParticipants(message.participants || []);
        if ((message.participants || []).length > 1 && roomData.is_owner) {
          const offer = await peer.createOffer();
          await peer.setLocalDescription(offer);
          sendSignal({ type: 'offer', offer });
        }
      }

      if (message.type === 'offer') {
        await peer.setRemoteDescription(new RTCSessionDescription(message.offer));
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        sendSignal({ type: 'answer', answer });
      }

      if (message.type === 'answer') {
        await peer.setRemoteDescription(new RTCSessionDescription(message.answer));
      }

      if (message.type === 'ice-candidate' && message.candidate) {
        await peer.addIceCandidate(new RTCIceCandidate(message.candidate));
      }

      if (message.type === 'participant-left') {
        setConnectionStatus('Participant left the room');
      }
    };

    socket.onerror = () => {
      setConnectionStatus('Signaling connection error');
      toast.error('Interview connection error');
    };

    socket.onclose = () => {
      setConnectionStatus('Room connection closed');
    };
  }, [createPeer, roomData, sendSignal, startLocalMedia]);

  const toggleAudio = () => {
    const audioTrack = localStreamRef.current?.getAudioTracks()[0];
    if (!audioTrack) return;
    audioTrack.enabled = !audioTrack.enabled;
    setIsMuted(!audioTrack.enabled);
  };

  const toggleVideo = () => {
    const videoTrack = localStreamRef.current?.getVideoTracks()[0];
    if (!videoTrack) return;
    videoTrack.enabled = !videoTrack.enabled;
    setIsVideoOff(!videoTrack.enabled);
  };

  const toggleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
        const sender = peerRef.current?.getSenders().find((item) => item.track?.kind === 'video');
        if (cameraTrack && sender) await sender.replaceTrack(cameraTrack);
        screenStreamRef.current?.getTracks().forEach((track) => track.stop());
        screenStreamRef.current = null;
        setIsScreenSharing(false);
        return;
      }

      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      screenStreamRef.current = screenStream;
      const screenTrack = screenStream.getVideoTracks()[0];
      const sender = peerRef.current?.getSenders().find((item) => item.track?.kind === 'video');
      if (sender) await sender.replaceTrack(screenTrack);
      if (localVideoRef.current) localVideoRef.current.srcObject = screenStream;
      screenTrack.onended = async () => {
        const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
        if (cameraTrack && sender) await sender.replaceTrack(cameraTrack);
        if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
        setIsScreenSharing(false);
      };
      setIsScreenSharing(true);
    } catch (err) {
      toast.error('Screen sharing was not started');
    }
  };

  const copyRoomLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success('Interview room link copied');
  };

  const addQuestionToNotes = () => {
    if (!question.trim()) return;
    setNotes((prev) => `${prev}${prev ? '\n' : ''}Question: ${question}\nNotes: `);
    setQuestion('');
  };

  const sendRoomMessage = () => {
    const content = roomMessage.trim();
    if (!content) return;
    sendSignal({ type: 'room-chat', message: content });
    setRoomMessages((current) => [...current, {
      id: `local-${Date.now()}`,
      type: 'room-chat',
      message: content,
      sender_id: user?.id,
      sender_name: user?.full_name || 'You',
    }]);
    setRoomMessage('');
  };

  const saveInterviewWorkspace = async (markComplete = false) => {
    setSavingWorkspace(true);
    try {
      const res = await api.put(`/video/room/${bookingId}/workspace`, {
        notes,
        scores,
        mark_complete: markComplete,
      });
      toast.success(res.data.diagnostic ? 'Camera and interview tools are ready' : markComplete ? 'Interview completed and notes saved' : 'Interview notes and scorecard saved');
      if (markComplete) {
        setRoomData((current) => ({ ...current, workspace: { ...(current.workspace || {}), status: 'completed' } }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the interview workspace');
    } finally {
      setSavingWorkspace(false);
    }
  };

  const otherParticipant = participants.find((participant) => participant.id !== user?.id);

  const handleLeave = useCallback(() => {
    socketRef.current?.close();
    peerRef.current?.close();
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    screenStreamRef.current?.getTracks().forEach((track) => track.stop());
    const role = localStorage.getItem('role');
    navigate(role === 'company' ? '/company/dashboard' : '/candidate/dashboard');
  }, [navigate]);

  useEffect(() => {
    loadRoom();
  }, [loadRoom]);

  useEffect(() => {
    if (roomData) connectSignaling();
  }, [roomData, connectSignaling]);

  useEffect(() => {
    return () => {
      socketRef.current?.close();
      peerRef.current?.close();
      localStreamRef.current?.getTracks().forEach((track) => track.stop());
      screenStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  if (isLoading) {
    return (
      <div className="h-screen bg-slate-950 flex items-center justify-center" data-testid="video-room-loading">
        <div className="text-center text-white">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-lg">Opening The Interview live room...</p>
          <p className="text-sm text-gray-400 mt-2">Please allow camera and microphone access.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-slate-950 flex items-center justify-center" data-testid="video-room-error">
        <div className="text-center text-white max-w-md">
          <PhoneOff size={40} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Unable to Connect</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={loadRoom} data-testid="retry-btn">Try Again</Button>
            <Button variant="destructive" onClick={handleLeave} data-testid="go-back-btn">Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-950 text-white flex flex-col" data-testid="video-room">
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="The Interview Logo" className="w-8 h-8" />
            <div>
              <h1 className="font-heading font-semibold">The Interview Live</h1>
              <p className="text-xs text-slate-400">Native browser video, screen share, notes, and scorecard.</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-2"><ShieldCheck size={16} /> In-site room</span>
            <span className="flex items-center gap-2"><Users size={16} /> {participants.length || 1} participant{participants.length === 1 ? '' : 's'}</span>
            <Button variant="outline" size="sm" className="bg-slate-800 border-slate-700 text-white" onClick={copyRoomLink}>
              <LinkIcon size={14} className="mr-2" />
              Copy Link
            </Button>
            <Button variant="outline" size="sm" className="bg-slate-800 border-slate-700 text-white" disabled={!otherParticipant} onClick={() => setShowSafetyReport(true)} data-testid="report-video-participant">
              <Flag size={14} className="mr-2" /> Report
            </Button>
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 grid grid-cols-[1fr_340px] gap-4 p-4">
        <section className="min-h-0 grid grid-rows-[1fr_auto] gap-4">
          <div className="grid md:grid-cols-2 gap-4 min-h-0">
            <div className="relative rounded border border-slate-800 bg-black overflow-hidden">
              <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" data-testid="remote-video" />
              <div className="absolute left-3 top-3 rounded bg-black/60 px-2 py-1 text-xs">Candidate / interviewer</div>
              {!remoteVideoRef.current?.srcObject && (
                <div className="absolute inset-0 flex items-center justify-center text-center text-slate-400">
                  <div>
                    <Users className="mx-auto mb-3" />
                    <p>{connectionStatus}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="relative rounded border border-slate-800 bg-black overflow-hidden">
              <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="local-video" />
              <div className="absolute left-3 top-3 rounded bg-black/60 px-2 py-1 text-xs">You</div>
              {isVideoOff && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900 text-slate-400">
                  <VideoOff />
                </div>
              )}
            </div>
          </div>

          <div className="rounded border border-slate-800 bg-slate-900 p-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button variant={isMuted ? 'destructive' : 'outline'} className={isMuted ? '' : 'bg-slate-800 border-slate-700 text-white'} onClick={toggleAudio} data-testid="toggle-audio-btn">
                {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
              </Button>
              <Button variant={isVideoOff ? 'destructive' : 'outline'} className={isVideoOff ? '' : 'bg-slate-800 border-slate-700 text-white'} onClick={toggleVideo} data-testid="toggle-video-btn">
                {isVideoOff ? <VideoOff size={18} /> : <VideoIcon size={18} />}
              </Button>
              <Button variant="outline" className={isScreenSharing ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800 border-slate-700 text-white'} onClick={toggleScreenShare} data-testid="share-screen-btn">
                {isScreenSharing ? <MonitorOff size={18} className="mr-2" /> : <Monitor size={18} className="mr-2" />}
                {isScreenSharing ? 'Stop Share' : 'Share Screen'}
              </Button>
              <Button variant="destructive" onClick={handleLeave} data-testid="leave-call-btn">
                <PhoneOff size={18} className="mr-2" />
                Leave
              </Button>
            </div>
            <div className="mt-3 text-center text-xs text-slate-400">{connectionStatus}</div>
          </div>
        </section>

        <aside className="min-h-0 overflow-y-auto rounded border border-slate-800 bg-slate-900 p-4 space-y-4">
          <div className="border border-emerald-900 bg-emerald-950/40 p-3 text-xs text-emerald-100">
            <p className="flex items-center gap-2 font-semibold"><ShieldCheck size={15} /> Safety protection active</p>
            <p className="mt-1 text-emerald-200">Room text is automatically screened. Camera and microphone feeds are not analyzed or recorded by safety moderation.</p>
          </div>
          <div>
            <h2 className="flex items-center gap-2 font-semibold"><Clipboard size={16} /> Interview Agenda</h2>
            <div className="mt-3 space-y-2 text-sm text-slate-300">
              <p>1. Confirm role interest and availability.</p>
              <p>2. Review experience, requirements, and pay range.</p>
              <p>3. Share screen for resume, portfolio, or job details.</p>
              <p>4. Capture next steps before leaving.</p>
            </div>
          </div>

          <div>
            <h2 className="flex items-center gap-2 font-semibold"><MessageSquare size={16} /> Room Chat</h2>
            <div className="mt-3 max-h-40 space-y-2 overflow-y-auto border border-slate-800 bg-slate-950 p-2" data-testid="video-room-chat">
              {roomMessages.length === 0 ? <p className="py-4 text-center text-xs text-slate-500">Share a link, spelling, or follow-up detail here.</p> : roomMessages.map((message) => (
                <div key={message.id || `${message.sender_id}-${message.created_at}`} className={`text-xs ${message.sender_id === user?.id ? 'text-blue-200' : 'text-slate-200'}`}>
                  <span className="font-semibold">{message.sender_name}: </span>{message.message}
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input value={roomMessage} onChange={(event) => setRoomMessage(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') sendRoomMessage(); }} className="min-w-0 flex-1 border border-slate-700 bg-slate-950 px-3 py-2 text-sm" placeholder="Message participant..." data-testid="video-room-chat-input" />
              <Button type="button" size="sm" onClick={sendRoomMessage} data-testid="send-video-room-chat"><Send size={15} /></Button>
            </div>
          </div>

          <div>
            <h2 className="flex items-center gap-2 font-semibold"><MessageSquare size={16} /> Question Builder</h2>
            <div className="mt-3 flex gap-2">
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                placeholder="Ask about schedule, skills, culture..."
              />
              <Button type="button" size="sm" onClick={addQuestionToNotes}>Add</Button>
            </div>
          </div>

          {roomData?.is_owner && (
            <>
              <div>
                <h2 className="flex items-center gap-2 font-semibold"><Star size={16} /> Interviewer Scorecard</h2>
                <div className="mt-3 space-y-3">
                  {scoreItems.map((item) => (
                    <div key={item}>
                      <div className="mb-1 flex justify-between text-xs text-slate-400">
                        <span>{item}</span>
                        <span>{scores[item]}/5</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="5"
                        value={scores[item]}
                        onChange={(e) => setScores((prev) => ({ ...prev, [item]: Number(e.target.value) }))}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-semibold">Private Interview Notes</h2>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={7}
                  className="mt-3 bg-slate-950 border-slate-700 text-white"
                  placeholder="Write interview notes, strengths, evidence, and follow-up tasks..."
                  data-testid="interview-notes"
                />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-white" disabled={savingWorkspace} onClick={() => saveInterviewWorkspace(false)} data-testid="save-interview-workspace">Save</Button>
                  <Button type="button" className="bg-emerald-600 hover:bg-emerald-500" disabled={savingWorkspace || roomData?.diagnostic} onClick={() => saveInterviewWorkspace(true)} data-testid="complete-interview">Complete</Button>
                </div>
              </div>
            </>
          )}
        </aside>
      </main>
      <SafetyReportDialog
        open={showSafetyReport}
        onOpenChange={setShowSafetyReport}
        subject={otherParticipant ? { id: otherParticipant.id, name: otherParticipant.name } : null}
        sourceType="video_interview"
        sourceId={bookingId}
      />
    </div>
  );
}
